import { IUnifiedPickerTabConfig } from '../unifiedColorPicker/unifiedColorPicker.types'
import {
  blackSatinRibbonsColorOptionsCategories,
  coffeBagsColorsOptionsCategories,
  whiteSatinRibbonsColorOptionsCategories,
} from './preselectedColroOption'

/** There are some products, the color configuration of which relies on 
    the value of the "Color" product option, instead of the "ColorS" option.
    This array is placed here as a workaround to identify such products.
    Prefarably, we need to rename this product option. But got to make sure it won't break anything at first.
  */
export const productsWithSingleColorProductOption = [
  'custom-recycled-satin-ribbon',
]

export type TColorOption =
  | '1 Color'
  | '2 Colors'
  | '3 Colors'
  | '1 Color (Full Background)'
  | '1 Color Full'
  | '4 Colors'
  | 'Full Color'
  | 'Multi Color (CMYK)'
  | 'White'
  | 'Black'
  | 'default'

export interface IUnifiedPickerConfig {
  pickerLabel: string
  pickerConfig: IUnifiedPickerTabConfig[]
  key: string
  defaultValue?: string
}

/**
 * [cap], [mode] and [pickerTemplate] props are required only for multi color pickers.
 */
export interface IBriefColorConfig {
  pickers: IUnifiedPickerConfig[]
  mode?: string
  cap?: number
  pickerTemplate?: IUnifiedPickerTabConfig[]
}

// Presets for pickers
const standartPickerConfig: IUnifiedPickerTabConfig[] = [
  {
    tabLabel: 'Standard',
    pickerType: 'hex',
  },
]
const standartAndPantonePickerConfig: IUnifiedPickerTabConfig[] = [
  {
    tabLabel: 'Standard',
    pickerType: 'hex',
  },
  {
    tabLabel: 'Pantone',
    pickerType: 'pantone-input',
  },
]

// Presets for config
const standartLogoAndBackgroundPicker = {
  pickers: [
    {
      pickerLabel: 'Logo',
      pickerConfig: standartPickerConfig,
      key: 'logo-color',
    },
    {
      pickerLabel: 'Background Color',
      pickerConfig: standartPickerConfig,
      key: 'background-color',
    },
  ],
}

const standartAndPantoneLogoPicker = {
  pickers: [
    {
      pickerLabel: 'Logo',
      pickerConfig: standartAndPantonePickerConfig,
      key: 'logo-color',
    },
  ],
}

const standartAndPantoneBackgroundColor = {
  pickers: [
    {
      pickerLabel: 'Background',
      pickerConfig: standartAndPantonePickerConfig,
      key: 'background-color',
    },
  ],
}

const standartAndPantoneLogoAndBgColorPikcer = {
  pickers: [
    ...standartAndPantoneLogoPicker.pickers,
    ...standartAndPantoneBackgroundColor.pickers,
  ],
}

const standartMultiColorPicker = {
  mode: 'manual',
  cap: 10,
  pickers: [],
  pickerTemplate: standartPickerConfig,
}

const standartAndPantoneMulticolorPicker = {
  mode: 'manual',
  cap: 10,
  pickers: [],
  pickerTemplate: standartAndPantonePickerConfig,
}

const getDynamicPantonePickerForCap = (cap = 1) => {
  return {
    mode: 'manual',
    cap: cap || 1,
    pickers: [],
    pickerTemplate: standartAndPantonePickerConfig,
  }
}

export const standartLogoPicker = {
  pickers: [
    {
      pickerLabel: 'Logo',
      pickerConfig: standartPickerConfig,
      key: 'logo-color',
    },
  ],
}

export const colorConfigPerProduct: Record<
  string,
  Record<TColorOption | string, IBriefColorConfig>
> = {
  'custom-printed-stickers': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-embosser': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-hang-tags': {
    default: standartLogoAndBackgroundPicker,
  },

  'custom-takeout-bags-with-handles': {
    default: standartLogoPicker,
    '1 Color': standartLogoPicker,
  },
  'custom-paper-shopping-bags': {
    default: standartLogoPicker,
    '1 Color': standartLogoPicker,
  },
  'wholesale-custom-paper-bags-with-handles': {
    default: standartLogoPicker,
    '1 Color': standartLogoPicker,
    '2 Colors': standartLogoAndBackgroundPicker,
    '2 Color': standartLogoAndBackgroundPicker,
    'Multi Color (CMYK)': standartMultiColorPicker,
  },
  'wholesale-custom-paper-bags-food-service': {
    default: standartLogoPicker,
    '1 Color': standartLogoPicker,
    '2 Colors': standartLogoAndBackgroundPicker,
    '2 Color': standartLogoAndBackgroundPicker,
    'Multi Color (CMYK)': standartMultiColorPicker,
  },

  'custom-eco-friendly-stickers': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-belly-bands': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-box-sleeves': {
    default: standartLogoAndBackgroundPicker,
  },

  'custom-coated-labels': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-menu-cards': {
    default: {
      pickers: [
        {
          pickerLabel: 'Font Color',
          pickerConfig: standartPickerConfig,
          key: 'font-color',
        },
        {
          pickerLabel: 'Background Color',
          pickerConfig: standartPickerConfig,
          key: 'background-color',
        },
      ],
    },
  },
  'custom-candle-labels': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-sticker-rolls': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-sticker-rolls-coated': {
    default: standartMultiColorPicker,
  },
  'custom-roll-labels': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-sandwich-bags': {
    default: standartMultiColorPicker,
  },
  'wholesale-custom-sos-bags': {
    default: standartLogoPicker,
    '1 Color': standartLogoPicker,
    '2 Colors': standartLogoAndBackgroundPicker,
    '2 Color': standartLogoAndBackgroundPicker,
    'Multi Color (CMYK)': standartMultiColorPicker,
  },
  'wholesale-custom-concession-bags': {
    default: standartLogoPicker,
    '1 Color': standartLogoPicker,
    '2 Colors': standartLogoAndBackgroundPicker,
    '2 Color': standartLogoAndBackgroundPicker,
    'Multi Color (CMYK)': standartMultiColorPicker,
  },
  'custom-coasters': {
    default: standartMultiColorPicker,
  },
  'custom-reverse-tuck-boxes': {
    default: standartMultiColorPicker,
  },
  'custom-straight-tuck-end-boxes': {
    default: standartMultiColorPicker,
  },
  'custom-gift-boxes': {
    default: standartMultiColorPicker,
  },
  'custom-boxes': {
    default: standartMultiColorPicker,
  },
  'custom-shoe-box': {
    default: standartMultiColorPicker,
  },
  'custom-candle-boxes': {
    default: standartMultiColorPicker,
  },
  'custom-regular-slotted-container': {
    default: standartMultiColorPicker,
  },
  'custom-paper-bags-with-handles': {
    default: standartMultiColorPicker,
  },

  'custom-tote-bags': {
    default: standartAndPantoneLogoPicker,
  },
  'custom-canvas-tote-bags': {
    default: standartAndPantoneLogoPicker,
  },
  'custom-drawstring-bags': {
    default: standartAndPantoneLogoPicker,
  },
  'custom-food-delivery-bags': {
    default: standartMultiColorPicker,
  },

  'custom-packing-tape': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-reinforced-gummed-packing-tape': {
    default: standartLogoAndBackgroundPicker,
  },
  'noissue-custom-washi-tape': {
    default: standartLogoAndBackgroundPicker,
  },
  'custom-masking-tape': {
    default: standartLogoAndBackgroundPicker,
  },

  'custom-printed-cards': {
    default: standartMultiColorPicker,
  },
  'custom-wine-carrier': {
    default: standartMultiColorPicker,
  },
  'custom-flyers': {
    default: standartMultiColorPicker,
  },
  'custom-foodsafe-paper': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    '1 Color Full': standartAndPantoneBackgroundColor,
  },
  'recycled-coated-paper': {
    default: standartAndPantoneLogoPicker,
  },
  'custom-coated-paper': {
    default: standartAndPantoneLogoPicker,
  },
  'custom-deli-paper': {
    default: standartAndPantoneLogoPicker,
  },
  'custom-sandwich-wrap-paper': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    '1 Color Full': standartAndPantoneBackgroundColor,
  },
  'custom-food-basket-liners': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    '1 Color Full': standartAndPantoneBackgroundColor,
  },
  'custom-poly-mailers': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '1 Color (Full Background)': standartAndPantoneBackgroundColor,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    'Full Color': standartAndPantoneMulticolorPicker,
    '4 Colors': standartAndPantoneMulticolorPicker,
    'Multi Color (CMYK)': standartAndPantoneMulticolorPicker,
  },
  'custom-kraft-mailers': {
    default: standartAndPantoneMulticolorPicker,
  },
  'custom-printed-tissue-paper': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    '1 Color (Full Background)': standartAndPantoneBackgroundColor,
  },
  'custom-multi-colored-tissue-paper': {
    default: standartAndPantoneLogoPicker,
  },
  'custom-kraft-tissue-paper': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    '1 Color (Full Background)': standartAndPantoneBackgroundColor,
  },
  'custom-die-cut-sticker-roll': {
    default: standartMultiColorPicker,
  },
  'custom-die-cut-stickers': {
    default: standartMultiColorPicker,
  },
  'custom-folded-hangtags': {
    default: standartMultiColorPicker,
  },
  'custom-folded-cards': {
    default: standartMultiColorPicker,
  },
  'custom-kraft-coffee-bags': {
    default: {
      pickers: [
        {
          pickerLabel: 'Logo Color',
          pickerConfig: [
            {
              tabLabel: 'List',
              pickerType: 'list',
              colorsList: coffeBagsColorsOptionsCategories.reduce(
                (list, category) => {
                  return [...list, ...category?.colors]
                },
                []
              ),
            },
          ],
          key: 'logo-color',
        },
      ],
    },
  },
  'custom-recycled-satin-ribbon': {
    default: {
      pickers: [
        {
          pickerLabel: 'Logo Color',
          pickerConfig: [
            {
              tabLabel: 'List',
              pickerType: 'list',
              colorsList: blackSatinRibbonsColorOptionsCategories.reduce(
                (list, category) => {
                  return [...list, ...category?.colors]
                },
                []
              ),
            },
          ],
          key: 'logo-color',
        },
      ],
    },
    White: {
      pickers: [
        {
          pickerLabel: 'Logo Color',
          pickerConfig: [
            {
              tabLabel: 'List',
              pickerType: 'list',
              colorsList: whiteSatinRibbonsColorOptionsCategories.reduce(
                (list, category) => {
                  return [...list, ...category?.colors]
                },
                []
              ),
            },
          ],
          key: 'logo-color',
        },
      ],
    },
    Black: {
      pickers: [
        {
          pickerLabel: 'Logo Color',
          pickerConfig: [
            {
              tabLabel: 'List',
              pickerType: 'list',
              colorsList: blackSatinRibbonsColorOptionsCategories.reduce(
                (list, category) => {
                  return [...list, ...category?.colors]
                },
                []
              ),
            },
          ],
          key: 'logo-color',
        },
      ],
    },
  },
  'custom-compostable-mailers': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
  },
  'wholesale-custom-compostable-mailers': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
  },
  'custom-recycled-die-cut-handle-bags': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '1 Color (Full Background)': standartAndPantoneBackgroundColor,
    'Multi Color (CMYK)': standartAndPantoneMulticolorPicker,
  },
  'custom-recycled-mailers-with-handles': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '1 Color (Full Background)': standartAndPantoneBackgroundColor,
    'Multi Color (CMYK)': standartAndPantoneMulticolorPicker,
  },
  'custom-single-wall-coffee-cups-recyclable': {
    default: standartMultiColorPicker,
  },
  'custom-double-wall-coffee-cups-recyclable': {
    default: standartMultiColorPicker,
  },
  'wholesale-custom-poly-mailers': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '1 Color (Full Background)': standartAndPantoneBackgroundColor,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    'Full Color': standartAndPantoneMulticolorPicker,
    '3 Colors': {
      mode: 'manual',
      cap: 3,
      pickers: [],
      pickerTemplate: standartAndPantonePickerConfig,
    },
    '4 Colors': {
      mode: 'manual',
      cap: 4,
      pickers: [],
      pickerTemplate: standartAndPantonePickerConfig,
    },
    'Multi Color (CMYK)': standartAndPantoneMulticolorPicker,
  },
  'wholesale-custom-cold-cups': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '1 Color (Full Background)': standartAndPantoneBackgroundColor,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    '3 Colors': {
      mode: 'manual',
      cap: 3,
      pickers: [],
      pickerTemplate: standartAndPantonePickerConfig,
    },
    '4 Colors': {
      mode: 'manual',
      cap: 4,
      pickers: [],
      pickerTemplate: standartAndPantonePickerConfig,
    },
  },
  'wholesale-custom-flexible-kraft-mailers': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
  },
  'custom-cake-boxes': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    'Multi Color (CMYK)': standartAndPantoneMulticolorPicker,
  },
  'custom-coffee-cup-sleeves': {
    default: standartAndPantoneLogoPicker,
    '1 Color': standartAndPantoneLogoPicker,
    '2 Colors': standartAndPantoneLogoAndBgColorPikcer,
    '3 Colors': {
      mode: 'manual',
      cap: 3,
      pickers: [],
      pickerTemplate: standartAndPantonePickerConfig,
    },
    'Multi Color (CMYK)': standartAndPantoneMulticolorPicker,
  },
  'wholesale-custom-pizza-boxes': {
    default: getDynamicPantonePickerForCap(1),
    '1 Color': getDynamicPantonePickerForCap(1),
    '2 Colors': getDynamicPantonePickerForCap(2),
    '3 Colors': getDynamicPantonePickerForCap(3),
    '4 Colors': getDynamicPantonePickerForCap(4),
  },
  'custom-coffee-bags': {
    default: standartMultiColorPicker,
  },
}
